import {
  Component,
  OnInit,
  Input,
  HostListener,
  ViewEncapsulation,
  Output,
  EventEmitter,
} from "@angular/core";
import * as Highcharts from "highcharts";
import { ChartPlotterService } from "../chart-plotter.service";
import { ChartPlotterDataService } from "../chart-plotter-data.service";
import { forkJoin, Observable, Subscription } from "rxjs";
import { AppService } from "src/app/app.service";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";

@Component({
  selector: "app-chart-plotter",
  templateUrl: "./chart-plotter.component.html",
  styleUrls: ["./chart-plotter.component.scss"],
  host: { class: "chart-plotter-component" },
  encapsulation: ViewEncapsulation.None,
})
export class ChartPlotterComponent implements OnInit {
  highcharts = Highcharts;
  chartOptions: Highcharts.Options;
  @Input() isRevisionStockPriceSelected = false;
  @Input() dataToUse: any;
  @Input() chartType: string;
  @Input() chartTypeDisplay: string;
  @Input() pageName = "charts";
  @Output() chartDataSet: EventEmitter<any> = new EventEmitter();
  @Output() chartStockPriceDataChange: EventEmitter<any> = new EventEmitter();
  @Output() onChartInstance: EventEmitter<Highcharts.Chart> =
    new EventEmitter();
  @Output() addinExportCalled: EventEmitter<any> = new EventEmitter();
  chartReady = false;
  showError = false;
  errorMessage = "";
  updateFlag = false;
  isRevisionChart = false;
  barChartResp: any;
  chart: Highcharts.Chart;
  displayData: {
    lineItemName: string;
    magnitude: string;
    currency?: string | null;
    firstEstimatedPeriod?: string | null;
    firstEstimatedQuarter?: string | null;
    stdDev?: number | null;
    consensusValue?: number | null;
    tableData?: any;
  };
  tsXdata: Array<string> = [];
  absValue: any = {};
  indexValue: any = {};
  typeValBar: any = {};
  respectivePeriodData: any = {};
  revisionChartSubscription;
  priceChartSubScription;
  dataCallObservable: Subscription;

  constructor(
    private service: ChartPlotterService,
    private dataService: ChartPlotterDataService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([infoCircle]);
  }
  // removed $event here, since it was breaking in build
  @HostListener("window:resize", [])
  sizeChange() {
    this.updateFlag = true;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
    }
  }

  ngOnInit() {
    Highcharts.setOptions(this.service.globalHighchartOptions);
    this.displayData = {
      lineItemName: "",
      magnitude: "",
    };
    this.service.addinExportEvent$.subscribe((event) => {
      this.addinExportCalled.emit(event);
    });
  }

  ngOnChanges() {
    this.getData();
  }

  getLogDetailsForFeature(featurePostfix) {
    if (this.pageName === "industry") {
      return `INDUSTRY_${featurePostfix}`;
    }
    return featurePostfix;
  }

  getLogDetailsForPageName() {
    if (this.pageName === "industry") {
      return "INDUSTRY_PAGE";
    }
    return "CHART_PAGE";
  }

  // Demonstrate chart instance
  logChartInstance(chart: Highcharts.Chart) {
    this.chart = chart;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
      // sometimes chart does not occupy all container width, triggering window resize to fix it
      // setTimeout(() => {
      //   window.dispatchEvent(new Event("resize"));
      // }, 200);
    }
  }

  getDataCallsForCharts(): Array<Observable<any>> {
    const dataCalls = this.dataToUse.tickers.map((t) => {
      // separate handling average calculation
      if (t === "Average") {
        const cid = [];
        for (const item of this.dataToUse.selectedTickersInfo) {
          const id = item.cid || item.company_id || item.companyid;
          if (id) {
            cid.push(id);
          }
        }
        const averagePayload = {
          cid,
          uniquesliid: this.dataToUse.lines,
          periods_list: this.dataToUse.periods,
          include_top_tickers: false,
          include_average: true,
          currency: this.dataToUse.currency,
          period_type: this.dataToUse.periodType,
        };
        return this.dataService.getChartsAverageData(averagePayload);
      }
      return this.dataService.getChartsData({
        ...this.dataToUse,
        ticker: t,
      });
    });
    return dataCalls;
  }

  getDataCallsForRevisionCharts(): Array<Observable<any>> {
    const dataCalls = [];
    const lineIds = this.dataToUse.lines;
    for (const lienId of lineIds) {
      if (this.dataToUse.tickers) {
        const companyId = [];
        let result = null;
        for (const tickerObj of this.dataToUse.selectedTickersInfo) {
          if (tickerObj.ticker !== "Average") {
            companyId.push(tickerObj.cid);
            const data = {
              ...this.dataToUse,
              ticker: tickerObj.ticker,
              lines: [lienId],
            };
            result = this.dataService.getChartsData(data);
            dataCalls.push(result);
          }
        }
        if (this.dataToUse.shouldShowAverage) {
          const avg_data = this.dataService.getTotalAvgRevisionChartData({
            companyids: companyId,
            period: this.dataToUse.periods[0] || this.dataToUse.otherPeriods[0],
            line_id: this.dataToUse.lines[0],
            revision: this.dataToUse.revision,
            currency: this.dataToUse.currency,
            industry_id: this.dataToUse.industryId,
            period_type: this.dataToUse.periodType || "absolute",
          });
          dataCalls.push(avg_data);
        }
      } else {
        const data = {
          ...this.dataToUse,
          lines: [lienId],
        };
        const result = this.dataService.getChartsData(data);
        dataCalls.push(result);
      }
    }
    return dataCalls;
  }

  tsYearFormatting(yearData: Array<string>) {
    const copy = [...yearData];
    const temp = yearData.map((data) => {
      const dataSplit = data.split("-");
      return `${dataSplit[1].replace("E", "")}-${dataSplit[0]}`;
    });
    temp.sort();
    const uniqueTemp = new Set([...temp]);
    const uniqueTempArr = Array.from(uniqueTemp);
    const data = uniqueTempArr.map(
      (item) => `${item.split("-")[1]}-${item.split("-")[0]}`,
    );
    const result = data.map((item) => {
      if (copy.includes(`${item}E`)) {
        return `${item}E`;
      }
      return item;
    });
    return result;
  }

  getSelectedSortedXPeriod(sortedPeriod, actualPeriod) {
    const result = [];
    // loop through sorted period, so that we can maintain the sorting order
    for (const item of sortedPeriod) {
      let nonEstimatedPeriodExist = false;
      let estimatedPeriodExist = false;
      const estimatedPeriod = `${item}E`;
      // loop through period with actual data, and check if estimated period exist or non estimated period and add according to it
      for (const uniqueItemPeriod of actualPeriod) {
        if (!nonEstimatedPeriodExist) {
          nonEstimatedPeriodExist = item === uniqueItemPeriod;
        }
        if (!estimatedPeriodExist) {
          estimatedPeriodExist = estimatedPeriod === uniqueItemPeriod;
        }
      }
      if (estimatedPeriodExist) {
        result.push(estimatedPeriod);
      } else if (nonEstimatedPeriodExist) {
        result.push(item);
      }
    }
    return result;
  }
  fetchDataForTimeseries() {
    if (this.dataCallObservable) {
      this.dataCallObservable.unsubscribe();
    }
    const datacalls = this.getDataCallsForCharts();
    if (datacalls.length === 0) {
      setTimeout(() => {
        this.chartOptions = {};
        this.chartReady = true;
      }, 0);
    } else {
      this.dataCallObservable = forkJoin(datacalls).subscribe((responses) => {
        responses = responses.filter((resp) => {
          return resp.status === "success";
        });
        this.absValue = {};
        this.typeValBar = {};
        this.tsXdata = [];
        this.respectivePeriodData = {};
        this.dataToUse.lines.forEach((l) => {
          this.dataToUse.tickers.forEach((t, index) => {
            if (responses[index].data[t]) {
              const lData =
                responses[index].data[t] && responses[index].data[t][l]
                  ? responses[index].data[t][l]
                  : {};
              if (
                lData["Value"] &&
                lData["Value"]["x"] &&
                lData["Value"]["x"].length
              ) {
                const xDataSet = new Set([
                  ...this.tsXdata,
                  ...lData["Value"]["x"],
                ]);
                this.tsXdata = Array.from(xDataSet);
              }
            }
          });
        });
        const uniqueXItems = [...this.tsXdata];
        this.dataToUse["x"] = this.getSelectedSortedXPeriod(
          this.dataToUse.periods,
          uniqueXItems,
        );

        this.dataToUse.lines.forEach((l) => {
          this.dataToUse.tickers.forEach((t, index) => {
            if (responses[index].data[t]) {
              const lData =
                responses[index].data[t] && responses[index].data[t][l]
                  ? responses[index].data[t][l]
                  : {};
              if (
                (Object.keys(lData).length === 0 &&
                  lData.constructor === Object) ||
                Object.keys(lData["Value"]).length === 0 ||
                !lData["Value"].data ||
                lData["Value"].data["normal"].length === 0
              ) {
                if (!(l in this.absValue)) {
                  this.absValue[l] = {};
                }
                if (!this.absValue[l]["value"]) {
                  this.absValue[l]["value"] = {};
                }
                if (!this.absValue[l]["value"][t]) {
                  this.absValue[l]["value"][t] = [];
                }
                this.absValue[l]["value"][t].push(null);
                if (!(l in this.typeValBar)) {
                  this.typeValBar[l] = {};
                }
                this.typeValBar[l][t] = [];
                if (!(l in this.respectivePeriodData)) {
                  this.respectivePeriodData[l] = {};
                }
                this.respectivePeriodData[l][t] = [];
              } else {
                lData["Value"].data["normal"].map((v) => {
                  if (!(l in this.absValue)) {
                    this.absValue[l] = {};
                    this.absValue[l]["value"] = {};
                  }
                  if (!this.absValue[l]["value"]) {
                    this.absValue[l]["value"] = {};
                  }
                  if (!this.absValue[l]["value"][t]) {
                    this.absValue[l]["value"][t] = [];
                  }
                  const xDataCopy = this.dataToUse["x"].map((data) =>
                    data.replace("E", ""),
                  );
                  const i = xDataCopy.indexOf(v[0].replace("E", ""));
                  this.absValue[l]["value"][t][i] = v[1];
                  this.absValue[l]["value"][t] = Array.from(
                    this.absValue[l]["value"][t],
                    (item) => {
                      if (item || item === 0) return item;
                      return null;
                    },
                  );
                  if (!(l in this.typeValBar)) {
                    this.typeValBar[l] = {};
                    this.typeValBar[l][t] = [];
                  }
                  if (!this.typeValBar[l][t]) {
                    this.typeValBar[l][t] = [];
                  }
                  this.typeValBar[l][t][i] = v[2];

                  // specific period of lineitem and array
                  if (!(l in this.respectivePeriodData)) {
                    this.respectivePeriodData[l] = {};
                    this.respectivePeriodData[l][t] = [];
                  }
                  if (!this.respectivePeriodData[l][t]) {
                    this.respectivePeriodData[l][t] = [];
                  }

                  return v[1];
                });

                const specificPeriodArray =
                  lData["Value"] &&
                  lData["Value"]["x"] &&
                  lData["Value"]["x"].length
                    ? lData["Value"]["x"]
                    : [];
                this.respectivePeriodData[l][t] = specificPeriodArray;
              }

              if (
                (Object.keys(lData).length === 0 &&
                  lData.constructor === Object) ||
                Object.keys(lData["Value"]).length === 0 ||
                lData["Value"].data["yoy"].length === 0
              ) {
                if (!(l in this.absValue)) {
                  this.absValue[l] = {};
                }
                if (!this.absValue[l]["yoy"]) {
                  this.absValue[l]["yoy"] = {};
                }
                if (!this.absValue[l]["yoy"][t]) {
                  this.absValue[l]["yoy"][t] = [];
                }
                this.absValue[l]["yoy"][t].push(null);
              } else {
                lData["Value"].data["yoy"].map((v) => {
                  if (!(l in this.absValue)) {
                    this.absValue[l] = {};
                  }
                  if (!this.absValue[l]["yoy"]) {
                    this.absValue[l]["yoy"] = {};
                  }
                  if (!this.absValue[l]["yoy"][t]) {
                    this.absValue[l]["yoy"][t] = [];
                  }
                  const xDataCopy = this.dataToUse["x"].map((data) =>
                    data.replace("E", ""),
                  );
                  const i = xDataCopy.indexOf(v[0].replace("E", ""));
                  this.absValue[l]["yoy"][t][i] = v[1];
                  this.absValue[l]["yoy"][t] = Array.from(
                    this.absValue[l]["yoy"][t],
                    (item) => {
                      if (item || item === 0) return item;
                      return null;
                    },
                  );
                  return v[1];
                });
              }
            }
          });
        });
        this.indexValue = {};
        for (const line in this.absValue) {
          for (
            let tickerIndex = 0;
            tickerIndex < this.dataToUse.tickers.length;
            tickerIndex++
          ) {
            const ticker = this.dataToUse.tickers;
            if (!this.indexValue[line]) {
              this.indexValue[line] = {};
            }
            let base =
              this.absValue &&
              this.absValue[line] &&
              this.absValue[line]["value"][ticker[tickerIndex]].filter((v) => {
                return v !== null && v !== 0;
              }).length
                ? this.absValue[line]["value"][ticker[tickerIndex]].filter(
                    (v) => {
                      return v !== null && v !== 0;
                    },
                  )[0]
                : null;
            let indexData = [];

            if (base === null) {
              indexData = [];
            } else {
              if (base < 0) {
                base = base * -1;
              }
              indexData = this.absValue[line]["value"][ticker[tickerIndex]].map(
                (val) => {
                  if (val === null) return null;
                  return (val / base) * 100;
                },
              );
            }

            if (!this.indexValue[line]["value"]) {
              this.indexValue[line]["value"] = {};
            }
            this.indexValue[line]["value"][ticker[tickerIndex]] = [
              ...indexData,
            ];
            let baseYoy =
              this.absValue &&
              this.absValue[line] &&
              this.absValue[line]["yoy"][ticker[tickerIndex]].filter((v) => {
                return v !== null && v !== 0;
              }).length
                ? this.absValue[line]["yoy"][ticker[tickerIndex]].filter(
                    (v) => {
                      return v !== null && v !== 0;
                    },
                  )[0]
                : null;
            let indexDataYoy = [];
            if (baseYoy < 0) {
              baseYoy = baseYoy * -1;
            }
            indexDataYoy = this.absValue[line]["yoy"][ticker[tickerIndex]].map(
              (val) => {
                if (val === null) return null;
                if (isFinite(base)) return (val / baseYoy) * 100;
                return null;
              },
            );
            if (!this.indexValue[line]["yoy"]) {
              this.indexValue[line]["yoy"] = {};
            }
            this.indexValue[line]["yoy"][ticker[tickerIndex]] = [
              ...indexDataYoy,
            ];
          }
        }
        this.dataToUse["absVal"] = {};
        this.dataToUse["indexVal"] = {};
        this.dataToUse["typeVal"] = {};
        this.dataToUse["respectivePeriodData"] = {};
        Object.assign(this.dataToUse["absVal"], this.absValue);
        Object.assign(this.dataToUse["indexVal"], this.indexValue);
        Object.assign(this.dataToUse["typeVal"], this.typeValBar);
        Object.assign(
          this.dataToUse["respectivePeriodData"],
          this.respectivePeriodData,
        );
        this.chartOptions = this.service.getChartsPageTimeseriesOptions(
          this.dataToUse,
        );
        this.chartReady = true;
        this.chartDataSet.emit(true);
      });
    }
  }

  fetchDataForRevision() {
    if (this.dataCallObservable) {
      this.dataCallObservable.unsubscribe();
    }
    const datacalls = this.getDataCallsForRevisionCharts();
    if (datacalls.length === 0) {
      setTimeout(() => {
        this.chartOptions = {};
        this.chartReady = true;
      }, 0);
    } else {
      this.dataCallObservable = forkJoin(datacalls).subscribe((responses) => {
        responses = responses.filter((resp) => {
          return resp.status === "success";
        });
        this.chartOptions = this.service.getChartsPageRevisionOptions(
          this.dataToUse,
          responses,
        );
        this.chartReady = true;
        this.chartDataSet.emit(true);
      });
    }
  }

  fetchDataForComparision() {
    if (this.dataCallObservable) {
      this.dataCallObservable.unsubscribe();
    }
    const datacalls = this.getDataCallsForCharts();
    if (datacalls.length === 0) {
      setTimeout(() => {
        this.chartOptions = {};
        this.chartReady = true;
      }, 0);
    } else {
      this.dataCallObservable = forkJoin(datacalls).subscribe((responses) => {
        responses = responses.filter((resp) => {
          return resp.status === "success";
        });
        this.barChartResp = responses;
        this.absValue = {};
        this.typeValBar = {};
        // this.dataToUse['isSecondaryAxis']=true;
        this.dataToUse.lines.forEach((l) => {
          this.dataToUse.tickers.forEach((t, index) => {
            if (responses[index].data[t]) {
              const lData = responses[index].data[t][l];
              if (
                (Object.keys(lData).length === 0 &&
                  lData.constructor === Object) ||
                !lData["Value"].data ||
                lData["Value"].data["normal"].length === 0
              ) {
                if (!(l in this.absValue)) {
                  this.absValue[l] = {};
                }
                if (!this.absValue[l]["value"]) {
                  this.absValue[l]["value"] = [];
                }
                this.absValue[l]["value"].push(null);
                if (!(l in this.typeValBar)) {
                  this.typeValBar[l] = [];
                }
                this.typeValBar[l].push(null);
              } else {
                lData["Value"].data["normal"].map((v) => {
                  if (!(l in this.absValue)) {
                    this.absValue[l] = {};
                    this.absValue[l]["value"] = [];
                  }
                  if (!this.absValue[l]["value"]) {
                    this.absValue[l]["value"] = [];
                  }
                  this.absValue[l]["value"].push(v[1]);
                  if (!(l in this.typeValBar)) {
                    this.typeValBar[l] = [];
                  }
                  this.typeValBar[l].push(v[2] || "");
                  return v[1];
                });
              }

              if (
                (Object.keys(lData).length === 0 &&
                  lData.constructor === Object) ||
                !lData["Value"].data ||
                lData["Value"].data["yoy"].length === 0
              ) {
                if (!(l in this.absValue)) {
                  this.absValue[l] = {};
                }
                if (!this.absValue[l]["yoy"]) {
                  this.absValue[l]["yoy"] = [];
                }
                this.absValue[l]["yoy"].push(null);
              } else {
                lData["Value"].data["yoy"].map((v) => {
                  if (!(l in this.absValue)) {
                    this.absValue[l] = {};
                  }
                  if (!this.absValue[l]["yoy"]) {
                    this.absValue[l]["yoy"] = [];
                  }
                  this.absValue[l]["yoy"].push(v[1]);
                  return v[1];
                });
              }
            }
          });
        });
        this.indexValue = {};
        for (const line in this.absValue) {
          this.indexValue[line] = {};
          let base =
            this.absValue &&
            this.absValue[line] &&
            this.absValue[line]["value"].length
              ? Math.max(
                  ...this.absValue[line]["value"].filter((v) => {
                    return v !== null && v !== 0;
                  }),
                )
              : null;
          let indexData = [];
          if (base < 0) {
            base = base * -1;
          }
          indexData = this.absValue[line]["value"].map((val) => {
            if (val === null) return null;
            return (val / base) * 100;
          });
          this.indexValue[line]["value"] = [...indexData];
          let baseYoy =
            this.absValue &&
            this.absValue[line] &&
            this.absValue[line]["yoy"].length
              ? Math.max(
                  ...this.absValue[line]["yoy"].filter((v) => {
                    return v !== null && v !== 0;
                  }),
                )
              : null;
          let indexDataYoy = [];
          if (baseYoy < 0) {
            baseYoy = baseYoy * -1;
          }
          indexDataYoy = this.absValue[line]["yoy"].map((val) => {
            if (val === null) return null;
            return (val / baseYoy) * 100;
          });
          this.indexValue[line]["yoy"] = [...indexDataYoy];
        }
        this.dataToUse["absVal"] = {};
        this.dataToUse["indexVal"] = {};
        this.dataToUse["typeVal"] = {};
        this.dataToUse["absVal"] = this.appService.superClone(this.absValue);
        this.dataToUse["indexVal"] = this.appService.superClone(
          this.indexValue,
        );
        this.dataToUse["typeVal"] = this.appService.superClone(this.typeValBar);
        // Object.assign(this.dataToUse["absVal"], ...this.absValue);
        // Object.assign(this.dataToUse["indexVal"], ...this.indexValue);
        // Object.assign(this.dataToUse["typeVal"], ...this.typeValBar);

        this.chartOptions = this.service.getChartsPageBarOptions(
          this.dataToUse,
        );
        this.chartReady = true;
        this.chartDataSet.emit(true);
      });
    }
  }

  getData() {
    this.chartReady = false;
    this.showError = false;
    this.isRevisionChart = false;
    this.errorMessage = "";
    if (this.dataToUse.selectedRevisionSources) {
      const unwantedSourceIndex =
        this.dataToUse.selectedRevisionSources.findIndex(
          (s) => s.toLowerCase() === "va actuals",
        );
      if (unwantedSourceIndex !== -1) {
        this.dataToUse.selectedRevisionSources[unwantedSourceIndex] =
          "Consensus";
        const uniqueSources = new Set(this.dataToUse.selectedRevisionSources);
        this.dataToUse.selectedRevisionSources = Array.from(uniqueSources);
      }
    }
    if (this.dataToUse.selectedSurpriseSources) {
      const unwantedSourceIndex =
        this.dataToUse.selectedSurpriseSources.findIndex(
          (s) => s.toLowerCase() === "va actuals",
        );
      if (unwantedSourceIndex !== -1) {
        this.dataToUse.selectedSurpriseSources[unwantedSourceIndex] =
          "Consensus";
        const uniqueSources = new Set(this.dataToUse.selectedSurpriseSources);
        this.dataToUse.selectedSurpriseSources = Array.from(uniqueSources);
      }
    }
    if (
      this.dataToUse.source &&
      this.dataToUse.source.toLowerCase() === "va actuals"
    ) {
      this.dataToUse.source = "Consensus";
    }

    switch (this.chartType) {
      case "industry-page-revision":
        if (!this.dataToUse.tickers || !this.dataToUse.dataReady) {
          return;
        }
        if (this.dataCallObservable) {
          this.dataCallObservable.unsubscribe();
        }
        this.dataCallObservable = forkJoin(
          this.getDataCallsForRevisionCharts(),
        ).subscribe((responses) => {
          responses = responses.filter((resp) => {
            return resp.status === "success";
          });
          this.chartOptions = this.service.getIndustryPageRevisionOptions(
            this.dataToUse,
            responses,
          );
          this.chartReady = true;
          this.chartDataSet.emit(true);
        });
        this.appService.logUserAction(
          {},
          "",
          "",
          this.getLogDetailsForFeature("CHARTS_DATA"),
          {
            chart_type: this.chartType,
            tickers: this.dataToUse.tickerIds || this.dataToUse.tickers,
            lines: this.dataToUse.lines,
            periods: this.dataToUse.periods,
            view_by:
              this.pageName === "industry"
                ? "revision"
                : this.dataToUse.dataView,
            revision: this.dataToUse.revision,
            currency: this.dataToUse.currency,
            scale_type: this.dataToUse.scaleType === 1 ? "abs" : "index",
            tab_name: this.dataToUse.tabName,
            industry_id: this.dataToUse.selectedIndustryId,
          },
          "",
          this.getLogDetailsForPageName(),
        );
        break;
      case "industry-page-total-revision":
      case "industry-page-average-revision":
        if (!this.dataToUse.tickers || !this.dataToUse.dataReady) {
          return;
        }
        this.dataService
          .getTotalAvgRevisionChartData({
            companyids: this.dataToUse.companyids,
            period: this.dataToUse.periods[0],
            line_id: this.dataToUse.lines[0],
            revision: this.dataToUse.revision,
            currency: this.dataToUse.currency,
            industry_id: this.dataToUse.industryId,
            period_type: this.dataToUse.periodType || "absolute",
          })
          .subscribe((response) => {
            this.chartOptions =
              this.service.getIndustryPageTotalAvgRevisionOptions(
                this.dataToUse,
                response,
                this.chartType,
              );
            this.chartReady = true;
            this.chartDataSet.emit(true);
          });
        this.appService.logUserAction(
          {},
          "",
          "",
          this.getLogDetailsForFeature("TOTAL_REVISION_CHARTS_DATA"),
          {
            chart_type: this.chartType,
            tickers: this.dataToUse.tickerIds || this.dataToUse.tickers,
            lines: this.dataToUse.lines,
            periods: this.dataToUse.periods,
            view_by:
              this.pageName === "industry"
                ? "revision"
                : this.dataToUse.dataView,
            revision: this.dataToUse.revision,
            currency: this.dataToUse.currency,
            scale_type: this.dataToUse.scaleType === 1 ? "abs" : "index",
            tab_name: this.dataToUse.tabName,
            industry_id: this.dataToUse.selectedIndustryId,
          },
          "",
          this.getLogDetailsForPageName(),
        );
        break;
      case "industry-page-timeseries":
        if (this.dataToUse.absVal && this.dataToUse.dataReady) {
          this.chartOptions = this.service.getIndustryPageTimeseriesOptions(
            this.dataToUse,
          );
          setTimeout(() => {
            this.chartReady = true;
            this.chartDataSet.emit(true);
          }, 0);
        }
        break;
      case "charts-page-revision":
      case "comparative-page-revision":
        if (this.dataToUse.operation !== undefined) {
          if (this.dataToUse.operation === "updateSeries") {
            if (!this.chartOptions) {
              this.fetchDataForRevision();
            } else {
              this.chartReady = true;
            }
            this.chartOptions.series.forEach((s) => {
              if (this.dataToUse.seriesInfo[s.name]) {
                s["data"].forEach((sd) => {
                  sd["visible"] = this.dataToUse.seriesInfo[s.name].visible;
                });
              }
            });
            this.chartOptions = { ...this.chartOptions };
          } else if (this.dataToUse.operation === "updateScale") {
            if (!this.chartOptions) {
              this.fetchDataForRevision();
            } else {
              this.chartReady = true;
            }
            this.chartOptions["series"][0]["isSecondaryAxis"] =
              this.dataToUse.isSecondaryAxis;
            this.chartOptions.series.forEach((s) => {
              s["scale"] = this.dataToUse.scaleType === 1 ? "abs" : "index";
              s["data"].forEach((sd) => {
                sd["y"] =
                  this.dataToUse.scaleType === 1 ? sd["abs"] : sd["ind"];
              });
            });
            this.chartOptions = { ...this.chartOptions };
          } else {
            this.fetchDataForRevision();
            this.appService.logUserAction(
              {},
              "",
              "",
              this.getLogDetailsForFeature("CHARTS_DATA"),
              {
                chart_type: this.chartType,
                tickers: this.dataToUse.tickers,
                lines: this.dataToUse.lines,
                periods: this.dataToUse.periods,
                view_by: this.dataToUse.dataView,
                revision: this.dataToUse.revision,
                currency: this.dataToUse.currency,
                scale_type: this.dataToUse.scaleType === 1 ? "abs" : "index",
              },
              "",
              this.getLogDetailsForPageName(),
            );
          }
        }
        break;
      case "charts-page-timeseries":
      case "comparative-page-timeseries":
        if (this.dataToUse.operation !== undefined) {
          if (this.dataToUse.operation === "updateSeries") {
            if (!this.chartOptions) {
              this.fetchDataForTimeseries();
            } else {
              this.chartReady = true;
            }
            this.chartOptions.series.forEach((s) => {
              if (this.dataToUse.seriesInfo[s.name]) {
                s["data"].forEach((sd) => {
                  sd["visible"] = this.dataToUse.seriesInfo[s.name].visible;
                });
              }
            });
            this.chartOptions = { ...this.chartOptions };
          } else if (this.dataToUse.operation === "updateScale") {
            if (!this.chartOptions) {
              this.fetchDataForTimeseries();
            } else {
              this.chartReady = true;
            }
            const data =
              this.dataToUse.scaleType === 1
                ? { ...this.absValue }
                : { ...this.indexValue };
            this.chartOptions.series.forEach((o) => {
              if (o["custom"] && o["custom"]["parentLine"]) {
                o["scale"] = this.dataToUse.scaleType === 1 ? "abs" : "index";
                this.chartOptions["series"][0]["isSecondaryAxis"] =
                  this.dataToUse.isSecondaryAxis;
                const line = o["custom"]["parentLine"];
                const ticker = o.name;
                data[line][this.dataToUse.dataView][ticker] &&
                  data[line][this.dataToUse.dataView][ticker].forEach(
                    (d, index) => {
                      o["data"][index]["y"] = d !== null ? d : null;
                    },
                  );
              }
            });
            this.chartOptions = { ...this.chartOptions };
          } else if (this.dataToUse.operation === "updateDv") {
            if (!this.chartOptions) {
              this.fetchDataForTimeseries();
            } else {
              this.chartReady = true;
            }
            let data = {};
            if (this.dataToUse.scaleType === 1) {
              data = this.absValue;
            } else {
              data = this.indexValue;
            }
            this.chartOptions["series"][0]["dv"] = this.dataToUse.dataView;
            this.chartOptions["series"][0]["isSecondaryAxis"] =
              this.dataToUse.isSecondaryAxis;
            this.chartOptions.series.map((o) => {
              const line = o.name.split("-")[0];
              const ticker = o.name.split("-")[1];
              data[line][this.dataToUse.dataView][ticker] &&
                data[line][this.dataToUse.dataView][ticker].map((d, index) => {
                  o["data"][index]["y"] = d !== null ? d : 0;
                  return d;
                });
              return o;
            });
            this.chartOptions = { ...this.chartOptions };
          } else {
            this.fetchDataForTimeseries();
            if (this.chartType === "charts-page-timeseries") {
              this.appService.logUserAction(
                {},
                "",
                "",
                this.getLogDetailsForFeature("CHARTS_DATA"),
                {
                  chart_type: this.chartType,
                  tickers: this.dataToUse.tickers,
                  lines: this.dataToUse.lines,
                  periods: this.dataToUse.periods,
                  view_by: this.dataToUse.dataView,
                  revision: this.dataToUse.revision,
                  currency: this.dataToUse.currency,
                  scale_type: this.dataToUse.scaleType === 1 ? "abs" : "index",
                },
                "",
                this.getLogDetailsForPageName(),
              );
            }
          }
        }
        break;
      case "charts-page-bar":
      case "comparative-page-comparison":
        if (this.dataToUse.operation !== undefined) {
          if (this.dataToUse.operation === "updateSeries") {
            if (!this.chartOptions) {
              this.fetchDataForComparision();
            } else {
              this.chartReady = true;
            }
            this.chartOptions.series.forEach((s) => {
              if (this.dataToUse.seriesInfo[s.name]) {
                const color = s.color;
                s["data"].forEach((sd) => {
                  sd["originalColor"] = color;
                  sd["visible"] = this.dataToUse.seriesInfo[s.name].visible;
                  if (this.dataToUse.seriesInfo[sd.name]) {
                    sd.color =
                      this.dataToUse.seriesInfo[sd.name]["visible"] === false
                        ? "#efefef"
                        : sd["originalColor"];
                  }
                });
              }
            });
            this.chartOptions = { ...this.chartOptions };
          } else if (this.dataToUse.operation === "updateScale") {
            if (!this.chartOptions) {
              this.fetchDataForComparision();
            } else {
              this.chartReady = true;
            }
            const data =
              this.dataToUse.scaleType === 1
                ? { ...this.absValue }
                : { ...this.indexValue };
            this.chartOptions["series"].map((option) => {
              option["scale"] =
                this.dataToUse.scaleType === 1 ? "abs" : "index";
              option["isSecondaryAxis"] = this.dataToUse.isSecondaryAxis;
              return option;
            });
            for (const line in data) {
              const obj = this.chartOptions.series.filter((o) => {
                return o["custom"].parentLine.toString() === line.toString();
              });
              data[line][this.dataToUse.dataView].map((d, index) => {
                obj[0]["data"][index]["y"] = d;
                return d;
              });
            }
            this.chartOptions = { ...this.chartOptions };
          } else if (this.dataToUse.operation === "updateDv") {
            if (!this.chartOptions) {
              this.fetchDataForComparision();
            } else {
              this.chartReady = true;
            }
            const data =
              this.dataToUse.scaleType === 1
                ? { ...this.absValue }
                : { ...this.indexValue };
            this.chartOptions["series"][0]["dv"] = this.dataToUse.dataView;
            this.chartOptions["series"][0]["isSecondaryAxis"] =
              this.dataToUse.isSecondaryAxis;
            for (const line in data) {
              //this.chartOptions['series']['data'] = {...this.chartOptions['series']['data'],y: data[line]}
              const obj = this.chartOptions.series.filter((o) => {
                return o["custom"].parentLine.toString() === line.toString();
              });
              const newData = data[line][this.dataToUse.dataView].filter(
                (r) => r !== null,
              );
              newData.map((d, index) => {
                obj[0]["data"][index]["y"] = d;
                return d;
              });
            }
            this.chartOptions = { ...this.chartOptions };
          } else {
            this.fetchDataForComparision();
            this.appService.logUserAction(
              {},
              "",
              "",
              this.getLogDetailsForFeature("CHARTS_DATA"),
              {
                chart_type: this.chartType,
                tickers: this.dataToUse.tickers,
                lines: this.dataToUse.lines,
                periods: this.dataToUse.periods,
                view_by: this.dataToUse.dataView,
                revision: this.dataToUse.revision,
                currency: this.dataToUse.currency,
                scale_type: this.dataToUse.scaleType === 1 ? "abs" : "index",
              },
              "",
              this.getLogDetailsForPageName(),
            );
          }
        }
        break;
      case "multi-company-revision":
        this.dataService
          .getMultiCompanyRevisionChartData(this.dataToUse)
          .subscribe(
            (response) => {
              if (response.status === "error") {
                this.chartReady = true;
                this.showError = true;
                throw Error(
                  "getMultiCompanyRevisionChartData throws status error",
                );
              }
              response["percentChange"] = this.dataToUse.percentChange;
              this.chartOptions = this.service.getRevisionChartOptions(
                response,
                this.chartTypeDisplay,
              );
              this.setChartReady(response);
            },
            (err) => {
              this.chartReady = true;
              this.showError = true;
              throw Error(err);
            },
          );
        break;
      case "multi-company-bar":
        this.dataService.getMultiCompanyBarChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.chartReady = true;
              this.showError = true;
              throw Error("getMultiCompanyBarChartData throws status error");
            }
            this.chartOptions =
              this.service.getMultiCompanyBarChartOptions(response);
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = true;
            throw Error(err);
          },
        );
        break;
      case "bubble":
        this.dataService
          .getMultiCompanyBubbleChartData(this.dataToUse)
          .subscribe(
            (response) => {
              if (response.status === "error") {
                this.chartReady = true;
                this.showError = true;
                throw Error(
                  "getMultiCompanyBubbleChartData throws status error",
                );
              }
              this.chartOptions =
                this.service.getMultiCompanyBubbleChartOptions(response);
              this.setChartReady(response);
            },
            (err) => {
              this.chartReady = true;
              this.showError = true;
              throw Error(err);
            },
          );
        break;
      case "mini-distribution-chart":
        this.chartOptions = this.service.getMiniDistributionDataOptions(
          this.dataToUse,
        );
        this.setChartReady();
        break;
      case "mini-revision-chart":
        this.dataService.getMiniRevisionChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.showError = false;
              throw Error("getMiniRevisionChartData throws status error");
            }
            this.chartOptions = this.service.getMiniRevisionDataOptions({
              ...this.dataToUse,
              ...response,
            });
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = false;
            throw Error(err);
          },
        );
        break;
      case "mini-trend-chart":
        this.dataService.getMiniTrendChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.showError = false;
              throw Error("getMiniRevisionChartData throws status error");
            }
            this.chartOptions = this.service.getMiniTrendDataOptions({
              ...this.dataToUse,
              ...response,
            });
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = false;
            throw Error(err);
          },
        );
        break;
      case "distribution-analysis":
        this.dataService
          .getDistributionAnalysisChartData(this.dataToUse)
          .subscribe(
            (response) => {
              if (response.status === "error") {
                this.chartReady = true;
                this.showError = true;
                throw Error(
                  "getDistributionAnalysisChartData throws status error",
                );
              }
              if (response.Value.x.length === 0) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
              const consensusValue = this.service.getFormattedValue(
                response,
                response.consensusvalue,
              );
              this.chartOptions =
                this.service.getDistributionAnalysisChartOptions(
                  {
                    ...this.dataToUse,
                    ...response,
                    consensusvalue: consensusValue,
                  },
                  this.chartTypeDisplay,
                );
              this.setChartReady(response);
            },
            (err) => {
              this.chartReady = true;
              this.showError = true;
              throw Error(err);
            },
          );
        break;
      case "distribution-range":
        this.dataService
          .getDistributionRangeChartData(this.dataToUse)
          .subscribe(
            (response) => {
              if (response.status === "error") {
                this.chartReady = true;
                this.showError = true;
                throw Error(
                  "getDistributionRangeChartData throws status error",
                );
              }
              if (
                response.Value.length === 0 ||
                response.Value.x.length === 0
              ) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
              this.chartOptions = this.service.getDistibutionRangeChartOptions(
                {
                  ...this.dataToUse,
                  ...response,
                },
                this.chartTypeDisplay,
              );
              this.setChartReady(response);
            },
            (err) => {
              this.chartReady = true;
              this.showError = true;
              throw Error(err);
            },
          );
        break;
      case "trend-bar":
        this.dataService.getTrendBarChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.chartReady = true;
              this.showError = true;
              throw Error("getTrendBarChartData throws status error");
            }
            if (response.Value.x.length === 0) {
              this.chartReady = true;
              this.showError = true;
              this.errorMessage = "No data available";
              return;
            }
            this.chartOptions = this.service.getTrendBarChartOptions(
              {
                ...this.dataToUse,
                ...response,
              },
              this.chartTypeDisplay,
            );
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = true;
            throw Error(err);
          },
        );
        break;
      case "revision-daily":
      case "revision-monthly":
      case "revision-dailypeers":
      case "revision-monthlypeers": {
        this.dataToUse.revisionVal =
          this.chartType === "revision-daily" ||
          this.chartType === "revision-dailypeers"
            ? "1"
            : "2";
        this.isRevisionChart = true;
        this.dataToUse["isRevisionPeers"] =
          this.chartType === "revision-dailypeers" ||
          this.chartType === "revision-monthlypeers"
            ? true
            : false;

        const isDailyOneYear =
          this.chartType === "revision-daily" ||
          this.chartType === "revision-dailypeers";
        const isMonthlyTwoYear =
          this.chartType === "revision-monthly" ||
          this.chartType === "revision-monthlypeers";

        if (this.revisionChartSubscription) {
          this.revisionChartSubscription.unsubscribe();
        }
        if (this.priceChartSubScription) {
          this.priceChartSubScription.unsubscribe();
        }

        this.revisionChartSubscription = this.dataService
          .getRevisionChartData(this.dataToUse)
          .subscribe(
            (response) => {
              if (response.status === "error") {
                this.chartReady = true;
                this.showError = true;
                throw Error("getRevisionChartData throws status error");
              }
              if (Object.keys(response.data).length === 0) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
              let noData = true;
              const keys = Object.keys(response.data);
              for (let i = 0; i < keys.length; i++) {
                if (response.data[keys[i]].length > 0) {
                  noData = false;
                  break;
                }
              }
              if (noData) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
              const minInterval = 24 * 3600 * 1000;
              if (this.isRevisionStockPriceSelected) {
                const dataKeys = Object.keys(response.data);
                let dataLength = 0;
                let largestDataArray = response.data[0];

                for (let i = 0; i < dataKeys.length; i++) {
                  if (response.data[dataKeys[i]].length > dataLength) {
                    dataLength = response.data[dataKeys[i]].length;
                    largestDataArray = response.data[dataKeys[i]];
                  }
                }

                const startDate = largestDataArray[0][0];
                const endDate =
                  largestDataArray[largestDataArray.length - 1][0];
                let requestObj = {
                  ticker: this.dataToUse.ticker,
                  year: this.dataToUse.revisionVal,
                  type: 1,
                  periodStartDate: startDate,
                  periodEndDate: endDate,
                };
                if (isMonthlyTwoYear) {
                  requestObj = {
                    ...requestObj,
                    type: 2,
                  };
                }

                this.priceChartSubScription = this.dataService
                  .getStockPriceChartData(requestObj)
                  .subscribe((stockResponse) => {
                    if (stockResponse.status === "success") {
                      let dailyOneYearStockPriceResponse = null;
                      let monthlyTwoYearStockPriceResponse = null;
                      if (isDailyOneYear) {
                        dailyOneYearStockPriceResponse = {
                          ...stockResponse,
                        };
                      }
                      if (isMonthlyTwoYear) {
                        monthlyTwoYearStockPriceResponse = {
                          ...stockResponse,
                        };
                      }
                      const stockPriceResponseData = stockResponse
                        ? { ...stockResponse.data }
                        : undefined;

                      const currentChartOptions =
                        this.service.getRevisionChartOptions(
                          {
                            ...this.dataToUse,
                            ...response,
                            chartType: this.chartType,
                          },
                          this.chartTypeDisplay,
                          stockPriceResponseData,
                          minInterval,
                        );
                      this.chartOptions = { ...currentChartOptions };
                      this.chartOptions["yAxis"][0] = {
                        ...this.chartOptions["yAxis"][0],
                        alignTicks: false,
                      };
                      this.chartOptions["yAxis"][1] = {
                        ...this.chartOptions["yAxis"][1],
                        gridLineWidth: 0,
                        alignTicks: false,
                      };
                      this.setChartReady(response);
                      this.chartStockPriceDataChange.emit({
                        "revision-daily": dailyOneYearStockPriceResponse,
                        "revision-monthly": monthlyTwoYearStockPriceResponse,
                      });
                    }
                  });
              } else {
                const currentChartOptions =
                  this.service.getRevisionChartOptions(
                    {
                      ...this.dataToUse,
                      ...response,
                      chartType: this.chartType,
                    },
                    this.chartTypeDisplay,
                    undefined,
                    minInterval,
                  );
                this.chartOptions = { ...currentChartOptions };
                this.setChartReady(response);
              }
            },
            (err) => {
              this.chartReady = true;
              this.showError = true;
              throw Error(err);
            },
          );

        break;
      }
      case "revision-analysis":
        this.dataService.getRevisionAnalysisChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.chartReady = true;
              this.showError = true;
              throw Error("getRevisionAnalysisChartData throws status error");
            }
            if (Object.keys(response.data).length === 0) {
              this.chartReady = true;
              this.showError = true;
              this.errorMessage = "No data available";
              return;
            }
            let noData = true;
            const keys = Object.keys(response.data);
            for (let i = 0; i < keys.length; i++) {
              if (response.data[keys[i]].length > 0) {
                noData = false;
                break;
              }
            }
            if (noData) {
              this.chartReady = true;
              this.showError = true;
              this.errorMessage = "No data available";
              return;
            }

            this.chartOptions = this.service.getRevisionAnalysisChartOptions(
              {
                ...this.dataToUse,
                ...response,
              },
              this.chartTypeDisplay,
            );
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = true;
            throw Error(err);
          },
        );
        break;
      case "surprise-analysis":
        this.dataService.getSurpriseAnalysisChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.chartReady = true;
              this.showError = true;
              throw Error("getSurpriseAnalysisChartData throws status error");
            }
            if (response.data) {
              if (Object.keys(response.data).length === 0) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
              let noData = true;
              const keys = Object.keys(response.data);
              for (let i = 0; i < keys.length; i++) {
                if (response.data[keys[i]].x.length > 0) {
                  noData = false;
                  break;
                }
              }
              if (noData) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
            }
            this.chartOptions = this.service.getSurpriseAnalysisChartOptions(
              {
                ...this.dataToUse,
                ...response,
              },
              this.chartTypeDisplay,
            );
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = true;
            throw Error(err);
          },
        );
        break;
      case "surprise-line":
        this.dataService.getSurpriseLineChartData(this.dataToUse).subscribe(
          (response) => {
            if (response.status === "error") {
              this.chartReady = true;
              this.showError = true;
              throw Error("getSurpriseLineChartData throws status error");
            }
            if (response.data) {
              if (Object.keys(response.data).length === 0) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
              let noData = true;
              const keys = Object.keys(response.data);
              for (let i = 0; i < keys.length; i++) {
                if (response.data[keys[i]].x.length > 0) {
                  noData = false;
                  break;
                }
              }
              if (noData) {
                this.chartReady = true;
                this.showError = true;
                this.errorMessage = "No data available";
                return;
              }
            }
            this.chartOptions = this.service.getSurpriseLineChartOptions(
              {
                ...this.dataToUse,
                ...response,
              },
              this.chartTypeDisplay,
            );
            this.setChartReady(response);
          },
          (err) => {
            this.chartReady = true;
            this.showError = true;
            throw Error(err);
          },
        );
        break;
      case "freshness-chart":
        this.dataService
          .getFreshnessData(this.dataToUse.ticker, this.dataToUse.period)
          .subscribe(
            (response) => {
              if (response.status === "error") {
                this.chartReady = true;
                this.showError = true;
                throw Error("getFreshnessData throws status error");
              }
              this.chartOptions = this.service.getFreshnessChartOptions(
                response,
                this.dataToUse,
                this.chartTypeDisplay,
              );
              this.setChartReady(response);
            },
            (err) => {
              this.chartReady = true;
              this.showError = true;
              throw Error(err);
            },
          );
        break;
      case "forecast-chart":
        this.dataService
          .getForecastData(this.dataToUse.ticker)
          .subscribe((response) => {
            if (response.status === "error") {
              this.chartReady = true;
              this.showError = true;
              throw Error("getFreshnessData throws status error");
            }
            this.chartOptions = this.service.getForecastChartOptions(
              response,
              this.chartTypeDisplay,
            );
            this.setChartReady(response);
          });
        break;
      default:
        break;
    }
    this.service.setPageName(this.pageName);
  }

  setChartReady(chartData?) {
    const isIE = /msie\s|trident\//i.test(window.navigator.userAgent);
    if (isIE) {
      this.chartReady = true;
      setTimeout(() => {
        const evt = document.createEvent("UIEvents");
        evt.initEvent("resize", true, false);
        window.dispatchEvent(evt);
      }, 0);
    } else {
      this.chartReady = true;
    }
    if (chartData) {
      this.displayData.lineItemName = chartData.parameter_name;
      this.displayData.magnitude = chartData.display_unit;
      this.displayData.currency = chartData.currency;
      if (chartData.first_estimated_year) {
        this.displayData.firstEstimatedPeriod = chartData.first_estimated_year;
        this.displayData.firstEstimatedQuarter =
          chartData.first_estimated_quarter;
      }
      if (chartData.deviation !== null && chartData.deviation !== undefined) {
        this.displayData.stdDev = chartData.deviation;
        this.displayData.consensusValue = this.service.getFormattedValue(
          chartData,
          chartData.consensusvalue,
        );
      }
      this.displayData.tableData = chartData;
      this.chartDataSet.emit(this.displayData);
    }
  }
}
